import React, { useState, useMemo, useEffect } from 'react';
import samsaraService from '../../services/samsara.service';
import DataTable, { Column } from '../../components/common/DataTable';
import TypeAhead, { TypeAheadOption } from '../../components/TypeAhead';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';

interface QueryParam {
  name: string;
  type: 'text' | 'number' | 'datetime' | 'select';
  label: string;
  placeholder?: string;
  options?: string[];
  description?: string;
  required?: boolean;
}

interface EndpointOption {
  id: string;
  url: string;
  queryParams?: QueryParam[];
  fetchFunction: (params?: Record<string, any>) => Promise<any>;
  supportsLimit?: boolean;  // Whether this endpoint accepts a limit parameter
}

const SamsaraAPI: React.FC = () => {
  const { setPageComponents } = usePermissionsMode();
  const [selectedEndpointId, setSelectedEndpointId] = useState<string>('');
  const [limit, setLimit] = useState<number>(50);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [allColumns, setAllColumns] = useState<string[]>([]);
  const [queryParams, setQueryParams] = useState<Record<string, any>>({});
  const [loadTime, setLoadTime] = useState<number | null>(null);
  const [error, setError] = useState<{ status?: number; message?: string; requestId?: string } | null>(null);

  // Register components with permissions context
  const pageComponentsList = useMemo(() => [
    { id: 'samsara-api-results-table', name: 'API Results Table' },
  ], []);

  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => {
      setPageComponents([]);
    };
  }, [pageComponentsList, setPageComponents]);

  // Define all available Samsara API endpoints from documentation
  const endpoints: EndpointOption[] = [
    // Core Endpoints
    {
      id: 'addresses',
      url: '/addresses',
      queryParams: [
        { name: 'after', type: 'text', label: 'After (Cursor)', placeholder: 'Pagination cursor' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('addresses', params)
    },
    {
      id: 'attributes',
      url: '/attributes',
      supportsLimit: false,  // This endpoint doesn't accept limit parameter
      queryParams: [
        {
          name: 'entityType',
          type: 'select',
          label: 'Entity Type *',
          required: true,
          options: ['asset', 'driver', 'vehicle'],
          description: 'Type of entity to get attributes for (asset, driver, or vehicle only)'
        }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('attributes', params)
    },
    {
      id: 'contacts',
      url: '/contacts',
      supportsLimit: true,
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('contacts', params)
    },
    {
      id: 'organization',
      url: '/me',
      supportsLimit: false,  // This endpoint returns a single object, not a list
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('me', params)
    },
    {
      id: 'tags',
      url: '/tags',
      queryParams: [
        { name: 'after', type: 'text', label: 'After (Cursor)', placeholder: 'Pagination cursor' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('tags', params)
    },
    {
      id: 'users',
      url: '/users',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('users', params)
    },
    {
      id: 'user-roles',
      url: '/user-roles',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('user-roles', params)
    },

    // Asset Endpoints - Vehicles
    {
      id: 'fleet-vehicles',
      url: '/fleet/vehicles',
      queryParams: [
        { name: 'tagIds', type: 'text', label: 'Tag IDs', placeholder: 'Comma-separated tag IDs' },
        { name: 'parentTagIds', type: 'text', label: 'Parent Tag IDs', placeholder: 'Comma-separated parent tag IDs' },
        { name: 'after', type: 'text', label: 'After (Cursor)', placeholder: 'Pagination cursor' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/vehicles', params)
    },
    {
      id: 'vehicle-stats',
      url: '/fleet/vehicles/stats',
      queryParams: [
        { name: 'time', type: 'datetime', label: 'Time *', placeholder: 'ISO 8601 timestamp', required: true }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/vehicles/stats', params)
    },
    {
      id: 'vehicle-stats-feed',
      url: '/fleet/vehicles/stats/feed',
      queryParams: [
        { name: 'after', type: 'text', label: 'After (Cursor)', placeholder: 'Pagination cursor' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/vehicles/stats/feed', params)
    },
    {
      id: 'vehicle-stats-history',
      url: '/fleet/vehicles/stats/history',
      queryParams: [
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'tagIds', type: 'text', label: 'Tag IDs', placeholder: 'Comma-separated' },
        { name: 'vehicleIds', type: 'text', label: 'Vehicle IDs', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/vehicles/stats/history', params)
    },
    {
      id: 'vehicle-locations',
      url: '/fleet/vehicles/locations',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/vehicles/locations', params)
    },
    {
      id: 'vehicle-locations-feed',
      url: '/fleet/vehicles/locations/feed',
      queryParams: [
        { name: 'after', type: 'text', label: 'After (Cursor)', placeholder: 'Pagination cursor' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/vehicles/locations/feed', params)
    },
    {
      id: 'vehicle-locations-history',
      url: '/fleet/vehicles/locations/history',
      queryParams: [
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'tagIds', type: 'text', label: 'Tag IDs', placeholder: 'Comma-separated' },
        { name: 'vehicleIds', type: 'text', label: 'Vehicle IDs', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/vehicles/locations/history', params)
    },

    // Asset Endpoints - Trailers
    {
      id: 'fleet-trailers',
      url: '/fleet/trailers',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/trailers', params)
    },

    // Asset Endpoints - Equipment
    {
      id: 'fleet-equipment',
      url: '/fleet/equipment',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/equipment', params)
    },
    {
      id: 'equipment-locations',
      url: '/fleet/equipment/locations',
      queryParams: [
        { name: 'tagIds', type: 'text', label: 'Tag IDs', placeholder: 'Comma-separated' },
        { name: 'equipmentIds', type: 'text', label: 'Equipment IDs', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/equipment/locations', params)
    },
    {
      id: 'equipment-locations-feed',
      url: '/fleet/equipment/locations/feed',
      queryParams: [
        { name: 'after', type: 'text', label: 'After (Cursor)', placeholder: 'Pagination cursor' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/equipment/locations/feed', params)
    },

    // Driver Endpoints
    {
      id: 'fleet-drivers',
      url: '/fleet/drivers',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/drivers', params)
    },
    {
      id: 'driver-vehicle-assignments',
      url: '/fleet/driver-vehicle-assignments',
      queryParams: [
        { name: 'driverIds', type: 'text', label: 'Driver IDs', placeholder: 'Comma-separated' },
        { name: 'vehicleIds', type: 'text', label: 'Vehicle IDs', placeholder: 'Comma-separated' },
        { name: 'startTime', type: 'datetime', label: 'Start Time', placeholder: 'ISO 8601 timestamp' },
        { name: 'endTime', type: 'datetime', label: 'End Time', placeholder: 'ISO 8601 timestamp' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/driver-vehicle-assignments', params)
    },

    // Fuel & Energy
    {
      id: 'fuel-energy-reports',
      url: '/fleet/reports/drivers/fuel-energy',
      queryParams: [
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'driverIds', type: 'text', label: 'Driver IDs', placeholder: 'Comma-separated' },
        { name: 'tagIds', type: 'text', label: 'Tag IDs', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/reports/drivers/fuel-energy', params)
    },
    {
      id: 'vehicle-idling',
      url: '/fleet/reports/vehicle/idling',
      queryParams: [
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'vehicleIds', type: 'text', label: 'Vehicle IDs', placeholder: 'Comma-separated' },
        { name: 'tagIds', type: 'text', label: 'Tag IDs', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/reports/vehicle/idling', params)
    },

    // Routes
    {
      id: 'fleet-routes',
      url: '/fleet/routes',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/routes', params)
    },
    {
      id: 'route-updates',
      url: '/fleet/routes/audit-logs/feed',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/routes/audit-logs/feed', params)
    },

    // Carrier Assignments
    {
      id: 'carrier-proposed-assignments',
      url: '/fleet/carrier-proposed-assignments',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/carrier-proposed-assignments', params)
    },

    // Documents & Forms
    {
      id: 'document-types',
      url: '/fleet/document-types',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/document-types', params)
    },
    {
      id: 'form-templates',
      url: '/form-templates',
      queryParams: [
        { name: 'formTemplateIds', type: 'text', label: 'Form Template IDs', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('form-templates', params)
    },

    // Issues
    {
      id: 'issues',
      url: '/issues',
      queryParams: [
        { name: 'issueIds', type: 'text', label: 'Issue IDs', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('issues', params)
    },
    {
      id: 'issues-stream',
      url: '/issues/stream',
      queryParams: [
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('issues/stream', params)
    },

    // Maintenance
    {
      id: 'work-orders',
      url: '/maintenance/work-orders',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('maintenance/work-orders', params)
    },
    {
      id: 'service-tasks',
      url: '/maintenance/service-tasks',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('maintenance/service-tasks', params)
    },
    {
      id: 'dvirs-stream',
      url: '/dvirs/stream',
      queryParams: [
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('dvirs/stream', params)
    },

    // Safety
    {
      id: 'safety-events',
      url: '/fleet/safety-events',
      queryParams: [
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'driverIds', type: 'text', label: 'Driver IDs', placeholder: 'Comma-separated' },
        { name: 'vehicleIds', type: 'text', label: 'Vehicle IDs', placeholder: 'Comma-separated' },
        { name: 'tagIds', type: 'text', label: 'Tag IDs', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/safety-events', params)
    },
    {
      id: 'cameras-media',
      url: '/cameras/media',
      queryParams: [
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'cameraIds', type: 'text', label: 'Camera IDs', placeholder: 'Comma-separated' },
        { name: 'eventTypes', type: 'text', label: 'Event Types', placeholder: 'Comma-separated' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('cameras/media', params)
    },
    {
      id: 'coach-assignments',
      url: '/coaching/driver-coach-assignments',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('coaching/driver-coach-assignments', params)
    },
    {
      id: 'coaching-sessions',
      url: '/coaching/sessions/stream',
      queryParams: [
        { name: 'endTime', type: 'datetime', label: 'End Time', placeholder: 'ISO 8601 timestamp' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('coaching/sessions/stream', params)
    },
    {
      id: 'speeding-intervals',
      url: '/speeding-intervals/stream',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('speeding-intervals/stream', params)
    },

    // HOS Clocks
    {
      id: 'hos-clocks',
      url: '/fleet/hos/clocks',
      queryParams: [
        { name: 'driverIds', type: 'text', label: 'Driver IDs', placeholder: 'Comma-separated driver IDs' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/hos/clocks', params)
    },
    {
      id: 'hos-logs',
      url: '/fleet/hos/logs',
      queryParams: [
        { name: 'driverIds', type: 'text', label: 'Driver IDs', placeholder: 'Comma-separated driver IDs' },
        { name: 'startTime', type: 'datetime', label: 'Start Time', placeholder: 'ISO 8601 timestamp' },
        { name: 'endTime', type: 'datetime', label: 'End Time', placeholder: 'ISO 8601 timestamp' }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/hos/logs', params)
    },

    // Trips
    {
      id: 'trips',
      url: '/fleet/trips',
      queryParams: [
        { name: 'vehicleIds', type: 'text', label: 'Vehicle IDs', placeholder: 'Comma-separated' },
        { name: 'driverIds', type: 'text', label: 'Driver IDs', placeholder: 'Comma-separated' },
        { name: 'startTime', type: 'datetime', label: 'Start Time *', placeholder: 'ISO 8601 timestamp', required: true },
        { name: 'endTime', type: 'datetime', label: 'End Time *', placeholder: 'ISO 8601 timestamp', required: true }
      ],
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/trips', params)
    },

    // Gateways
    {
      id: 'gateways',
      url: '/fleet/gateways',
      fetchFunction: async (params) => samsaraService.callSamsaraAPI('fleet/gateways', params)
    },
  ];

  // Convert endpoints to TypeAhead options - show URL instead of label
  const endpointOptions: TypeAheadOption[] = useMemo(() => {
    return endpoints.map(endpoint => ({
      value: endpoint.id,
      label: endpoint.url
    }));
  }, []);

  // Get the selected endpoint object
  const selectedEndpoint = useMemo(() => {
    return endpoints.find(e => e.id === selectedEndpointId) || null;
  }, [selectedEndpointId]);

  // Reset query params when endpoint changes
  React.useEffect(() => {
    setQueryParams({});
  }, [selectedEndpointId]);

  // Extract all unique column names from data
  const extractColumns = (data: any[]): string[] => {
    if (!data || data.length === 0) return [];

    const columnSet = new Set<string>();

    const extractKeysRecursive = (obj: any, prefix = '') => {
      Object.keys(obj).forEach(key => {
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          // For nested objects, create flattened column names
          extractKeysRecursive(obj[key], fullKey);
        } else {
          columnSet.add(fullKey);
        }
      });
    };

    data.forEach(item => extractKeysRecursive(item));
    return Array.from(columnSet).sort();
  };

  // Get nested value from object using dot notation
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Format value for display
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  };

  // Check if all required parameters are filled
  const areRequiredParamsFilled = useMemo(() => {
    if (!selectedEndpoint?.queryParams) return true;

    const requiredParams = selectedEndpoint.queryParams.filter(param => param.required);
    return requiredParams.every(param => queryParams[param.name] && queryParams[param.name].trim() !== '');
  }, [selectedEndpoint, queryParams]);

  // Fetch data from selected endpoint
  const handleFetch = async () => {
    if (!selectedEndpoint) return;
    if (!areRequiredParamsFilled) return;

    setLoading(true);
    setError(null);  // Clear previous errors
    const startTime = performance.now();
    try {
      // Only add limit parameter if endpoint supports it (default to true for backwards compatibility)
      const params = selectedEndpoint.supportsLimit !== false
        ? { ...queryParams, limit }
        : { ...queryParams };
      const response = await selectedEndpoint.fetchFunction(params);
      const endTime = performance.now();
      setLoadTime(endTime - startTime);

      // Response structure is { data: [...], pagination: {...} }
      const fetchedData = response.data || [];

      if (Array.isArray(fetchedData)) {
        setData(fetchedData);
        const columns = extractColumns(fetchedData);
        setAllColumns(columns);
      } else {
        setData([]);
        setAllColumns([]);
      }
    } catch (error: any) {

      // Extract error details from response
      const errorData = error.response?.data;

      // Try to get the most specific error message
      let errorMessage = 'An unknown error occurred';
      let requestId = undefined;

      // Check if errorData has a detail.message that is a JSON string (double-encoded)
      if (errorData?.detail?.message && typeof errorData.detail.message === 'string') {
        try {
          const parsedError = JSON.parse(errorData.detail.message);
          errorMessage = parsedError.message || errorMessage;
          requestId = parsedError.requestId;
        } catch {
          // If parsing fails, just use the message as-is
          errorMessage = errorData.detail.message;
        }
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
        requestId = errorData.requestId;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (error.message && error.message !== 'Request failed with status code ' + error.response?.status) {
        errorMessage = error.message;
      } else {
        errorMessage = `Request failed with status code ${error.response?.status}`;
      }

      setError({
        status: error.response?.status,
        message: errorMessage,
        requestId: requestId || errorData?.requestId
      });

      setData([]);
      setAllColumns([]);
      setLoadTime(null);
    }
    setLoading(false);
  };

  // Build columns for DataTable
  const tableColumns: Column<any>[] = useMemo(() => {
    return allColumns.map(col => ({
      key: col,
      label: col.split('.').pop() || col,
      sortable: true,
      render: (row: any) => {
        const value = getNestedValue(row, col);
        return <span className="text-body">{formatValue(value)}</span>;
      },
      className: 'text-sm',
    }));
  }, [allColumns]);

  // Search filter function
  const searchFilter = (query: string) => (row: any) => {
    const searchLower = query.toLowerCase();
    return allColumns.some(col => {
      const value = getNestedValue(row, col);
      return formatValue(value).toLowerCase().includes(searchLower);
    });
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pb-6">
      {/* Header Controls */}
      <div className="px-4 sm:px-6 pt-6 pb-4">
        <div className="bg-panel rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-heading mb-6">
            Samsara API Explorer
          </h1>

          {/* Endpoint Selector and Limit */}
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1">
              <TypeAhead
                label="Select Samsara API Endpoint"
                options={endpointOptions}
                value={selectedEndpointId}
                onChange={setSelectedEndpointId}
                placeholder="Search for an endpoint (e.g., /fleet/vehicles)..."
                className="w-full"
              />
            </div>
            <div className="md:w-48">
              <label className="block text-sm font-medium text-label mb-2">
                Result Limit
              </label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="w-full px-4 py-2.5 bg-panel border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-body focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={512}>512 (Max)</option>
              </select>
            </div>
          </div>

          {/* Query Parameters */}
          {selectedEndpoint?.queryParams && selectedEndpoint.queryParams.length > 0 && (
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-label mb-3">
                Query Parameters
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {selectedEndpoint.queryParams.map(param => (
                  <div key={param.name}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {param.label}
                    </label>
                    {param.type === 'select' && param.options ? (
                      <select
                        value={queryParams[param.name] || ''}
                        onChange={(e) => setQueryParams({ ...queryParams, [param.name]: e.target.value })}
                        required={param.required}
                        className="w-full px-3 py-2 bg-panel border border-gray-300 dark:border-gray-600 rounded text-sm text-label focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select...</option>
                        {param.options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : param.type === 'datetime' ? (
                      <input
                        type="datetime-local"
                        value={
                          queryParams[param.name]
                            ? (() => {
                                try {
                                  return new Date(queryParams[param.name]).toISOString().slice(0, 16);
                                } catch {
                                  return '';
                                }
                              })()
                            : ''
                        }
                        onChange={(e) => {
                          // Convert local datetime to ISO 8601 with Z suffix
                          const isoString = e.target.value ? new Date(e.target.value).toISOString() : '';
                          setQueryParams({ ...queryParams, [param.name]: isoString });
                        }}
                        required={param.required}
                        className="w-full px-3 py-2 bg-panel border border-gray-300 dark:border-gray-600 rounded text-sm text-label focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <input
                        type={param.type === 'number' ? 'number' : 'text'}
                        value={queryParams[param.name] || ''}
                        onChange={(e) => setQueryParams({ ...queryParams, [param.name]: e.target.value })}
                        placeholder={param.placeholder}
                        required={param.required}
                        className="w-full px-3 py-2 bg-panel border border-gray-300 dark:border-gray-600 rounded text-sm text-label placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                    {param.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{param.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fetch Button */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <button
                onClick={handleFetch}
                disabled={!selectedEndpoint || loading || !areRequiredParamsFilled}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Fetching...' : 'Fetch Data'}
              </button>

              {selectedEndpoint && (
                <div className="text-sm text-gray-600 dark:text-gray-400 ml-auto">
                  Selected: <span className="font-medium">{selectedEndpoint.url}</span>
                </div>
              )}
            </div>
            {!areRequiredParamsFilled && selectedEndpoint?.queryParams?.some(p => p.required) && (
              <p className="text-xs text-red-600 dark:text-red-400">
                * Required parameters must be filled before fetching
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error Card */}
      {error && (
        <div className="px-4 sm:px-6 mb-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <div className="flex items-start">
              <div className="shrink-0">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                  {error.status ? `Error ${error.status}` : 'Request Failed'}
                </h3>
                <div className="text-sm text-red-700 dark:text-red-300 space-y-2">
                  <p className="font-medium">{error.message}</p>
                  {error.requestId && (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Request ID: <span>{error.requestId}</span>
                    </p>
                  )}
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                    This error was returned by the Samsara API. Check the endpoint documentation for required parameters and valid values.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-4 shrink-0 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      {data.length > 0 && (
        <div className="px-4 sm:px-6 lg:px-8">
          <DataTable
            title={selectedEndpoint?.url || 'Results'}
            data={data}
            columns={tableColumns}
            searchable={true}
            searchPlaceholder="Search all fields..."
            onSearch={searchFilter}
            rowsPerPage={10}
            showPerPageDropdown={true}
            perPageOptions={[10, 25, 50, 100]}
            showFieldsToggle={true}
            showLoadTime={true}
            loadTime={loadTime}
            getRowKey={(row: any) => row.id || row.driverId || row.vehicleId || row.serial || Math.random().toString()}
            className="mt-0 mx-0"
            componentId="samsara-api-results-table"
          />
        </div>
      )}

      {/* Empty State */}
      {!loading && data.length === 0 && selectedEndpoint && (
        <div className="px-4 sm:px-6">
          <div className="bg-panel rounded-lg shadow p-12 text-center">
            <p className="text-muted">
              No data returned from endpoint. Try fetching again or select a different endpoint.
            </p>
          </div>
        </div>
      )}

      {/* Initial State */}
      {!loading && data.length === 0 && !selectedEndpoint && (
        <div className="px-4 sm:px-6">
          <div className="bg-panel rounded-lg shadow p-12 text-center">
            <p className="text-muted text-lg mb-2">
              Select a Samsara API endpoint from above to get started
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              Choose an endpoint, configure query parameters (if available), and click Fetch Data
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SamsaraAPI;
